import * as React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import { useEffect, useMemo, useState } from "react";
import { Box, FormControlLabel, Switch, TextField } from "@mui/material";
import GeneralLoading from "../../../shared-components/Loading";
import { useApiClient } from "../../../api";
import { usePagination } from "../../../hooks/usePagination";

const SchoolGroupSearchAutocomplete = ({
  schoolGroup,
  onChange,
  children,
  showResults = false,
  subscriptionSwitch = false,
  sx = {},
}) => {
  const hasSingleSelect = typeof onChange === "function";
  if (!hasSingleSelect && !showResults) {
    throw new Error(
      "SchoolSearchAutocomplete requires either 'school' + 'onChange' for selection, or 'onResultsChange' for filtered results.",
    );
  }

  const [inputValue, setInputValue] = useState("");
  const [subscribedOnly, setSubscribedOnly] = useState(false);

  const [debouncedValue, setDebouncedValue] = useState(inputValue);

  // Update debouncedValue after 300ms of inactivity
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(inputValue);
    }, 200);

    return () => {
      clearTimeout(handler);
    };
  }, [inputValue]);
  const ApiClient = useApiClient();
  const filters = useMemo(
    () => ({
      searchString: debouncedValue,
      baseSearch: showResults,
      subscribedOnly,
    }),
    [debouncedValue, showResults, subscribedOnly],
  );

  const { options, loading, page, pageInfo, handlePageChange } = usePagination(
    ({ first, after, ...filters }) =>
      ApiClient.schools.searchSchoolGroups({ first, after, ...filters }),
    20,
    filters,
  );

  useEffect(() => {
    if (!schoolGroup) {
      setInputValue("");
    }
  }, [schoolGroup]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          gap: 10,
          ...sx,
        }}
      >
        <Autocomplete
          sx={{ flexGrow: 1 }}
          clearOnBlur={false}
          clearOnEscape={false}
          value={schoolGroup}
          onChange={onChange}
          inputValue={inputValue}
          filterOptions={(x) => x} // return options as-is
          onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
          options={options}
          open={hasSingleSelect ? undefined : false}
          disableCloseOnSelect={!hasSingleSelect}
          getOptionLabel={(option) => option.name} // adapt if API returns differently
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Box sx={{ fontWeight: 500 }}>{option.name}</Box>
                <Box sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                  {option.addressLine1} ({option.town})
                </Box>
              </Box>
            </li>
          )}
          loading={loading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Search for a school group"
              variant="outlined"
              fullWidth
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? (
                      <GeneralLoading
                        direction="row"
                        size={25}
                        message="Searching..."
                        sx={{ mr: 1 }}
                      />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
        {subscriptionSwitch && (
          <FormControlLabel
            control={
              <Switch
                checked={subscribedOnly}
                onChange={(e) => setSubscribedOnly(e.target.checked)}
              />
            }
            label="Subscribed only"
          />
        )}
      </Box>

      {children?.({ options, loading, page, pageInfo, handlePageChange })}
    </>
  );
};

export default SchoolGroupSearchAutocomplete;
